import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  ClipboardCheck, Plus, CheckCircle, Circle, Clock, Shield, Filter,
  ChevronDown, ChevronUp, Camera, Hash, FileText, MessageSquare,
  Trash2, User, AlertCircle, X
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  pending: { color: '#94A3B8', label: 'Pendiente', icon: Circle },
  in_progress: { color: '#3B82F6', label: 'En progreso', icon: Clock },
  completed: { color: '#10B981', label: 'Completado', icon: CheckCircle },
  approved: { color: '#8B5CF6', label: 'Aprobado', icon: Shield },
};

const CATEGORY_LABELS = {
  commissioning: 'Comisionamiento', quality: 'Calidad', safety: 'Seguridad',
  delivery: 'Recepción', maintenance: 'Mantenimiento',
};

const ITEM_ICONS = { check: CheckCircle, text: FileText, number: Hash, photo: Camera };

export default function Checklists() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [checklists, setChecklists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChecklist, setActiveChecklist] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ templateId: '', name: '', assignedTo: '' });
  const [users, setUsers] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');

  const canEdit = ['superadmin', 'admin_empresa', 'project_manager', 'supervisor'].includes(user?.role);

  useEffect(() => {
    Promise.all([
      api.get('/projects').then(r => setProjects(r.data.data || r.data || [])),
      api.get('/checklists/templates').then(r => setTemplates(r.data)),
      api.get('/users', { params: { limit: 200 } }).then(r => setUsers(r.data.data || r.data || [])),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadChecklists();
  }, [selectedProject, filterStatus]);

  const loadChecklists = () => {
    const params = {};
    if (selectedProject) params.project_id = selectedProject;
    if (filterStatus) params.status = filterStatus;
    api.get('/checklists', { params }).then(r => setChecklists(r.data)).catch(() => {});
  };

  const createChecklist = async () => {
    if (!selectedProject) return toast.error('Selecciona un proyecto');
    try {
      const payload = {
        projectId: selectedProject,
        templateId: createForm.templateId || undefined,
        name: createForm.name || undefined,
        assignedTo: createForm.assignedTo || undefined,
      };
      await api.post('/checklists', payload);
      toast.success('Checklist creada');
      setShowCreate(false);
      setCreateForm({ templateId: '', name: '', assignedTo: '' });
      loadChecklists();
    } catch (e) { toast.error('Error creando checklist'); }
  };

  const loadDetail = async (id) => {
    try {
      const { data } = await api.get(`/checklists/${id}`);
      setActiveChecklist(data);
    } catch (e) { toast.error('Error cargando checklist'); }
  };

  const updateResponse = async (itemIndex, field, value) => {
    if (!activeChecklist) return;
    const responses = [...(activeChecklist.responses || [])];
    if (!responses[itemIndex]) {
      responses[itemIndex] = { itemIndex, checked: false, value: null };
    }
    responses[itemIndex][field] = value;
    responses[itemIndex].respondedBy = user.id;
    responses[itemIndex].respondedAt = new Date().toISOString();

    try {
      const { data } = await api.put(`/checklists/${activeChecklist.id}`, { responses });
      setActiveChecklist(data);
      setChecklists(prev => prev.map(c => c.id === data.id ? { ...c, ...data } : c));
    } catch (e) { toast.error('Error'); }
  };

  const approveChecklist = async () => {
    try {
      const { data } = await api.put(`/checklists/${activeChecklist.id}`, { status: 'approved' });
      setActiveChecklist(data);
      setChecklists(prev => prev.map(c => c.id === data.id ? { ...c, ...data } : c));
      toast.success('Checklist aprobada');
    } catch (e) { toast.error('Error'); }
  };

  const deleteChecklist = async (id) => {
    if (!window.confirm('¿Eliminar esta checklist?')) return;
    try {
      await api.delete(`/checklists/${id}`);
      setChecklists(prev => prev.filter(c => c.id !== id));
      if (activeChecklist?.id === id) setActiveChecklist(null);
      toast.success('Eliminada');
    } catch (e) { toast.error('Error'); }
  };

  const items = activeChecklist?.template_items || [];
  const responses = activeChecklist?.responses || [];

  return (
    <div className="checklists-page">
      <div className="page-header">
        <div>
          <h1><ClipboardCheck size={24} /> Checklists</h1>
          <p className="subtitle">Listas de verificación y comisionamiento</p>
        </div>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} /> Nueva Checklist
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <select className="filter-select" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
          <option value="">Todos los proyectos</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
        </select>
        <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="checklists-layout">
        {/* List */}
        <div className="checklists-list">
          {checklists.length === 0 && !loading && (
            <div className="empty-state"><ClipboardCheck size={48} /><h3>Sin checklists</h3><p>Crea una checklist desde una plantilla</p></div>
          )}
          {checklists.map(cl => {
            const cfg = STATUS_CONFIG[cl.status] || STATUS_CONFIG.pending;
            const Icon = cfg.icon;
            const pct = cl.total_items > 0 ? Math.round(cl.completed_items / cl.total_items * 100) : 0;
            return (
              <div key={cl.id}
                className={`checklist-card ${activeChecklist?.id === cl.id ? 'active' : ''}`}
                onClick={() => loadDetail(cl.id)}>
                <div className="cl-card-header">
                  <Icon size={16} style={{ color: cfg.color }} />
                  <span className="cl-card-name">{cl.name}</span>
                  <span className="cl-badge" style={{ background: cfg.color + '20', color: cfg.color }}>{cfg.label}</span>
                </div>
                <div className="cl-card-meta">
                  <span>{CATEGORY_LABELS[cl.category] || cl.category}</span>
                  <span>{cl.project_code}</span>
                  {cl.assigned_to_name && <span><User size={10} /> {cl.assigned_to_name}</span>}
                </div>
                <div className="cl-card-progress">
                  <div className="progress-bar-sm">
                    <div className="progress-fill-sm" style={{ width: `${pct}%`, background: cfg.color }} />
                  </div>
                  <span className="cl-pct">{cl.completed_items}/{cl.total_items} ({pct}%)</span>
                </div>
                {canEdit && (
                  <button className="cl-delete" onClick={e => { e.stopPropagation(); deleteChecklist(cl.id); }}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Detail */}
        <div className="checklist-detail">
          {!activeChecklist ? (
            <div className="empty-state"><ClipboardCheck size={48} /><p>Selecciona una checklist</p></div>
          ) : (
            <>
              <div className="cl-detail-header">
                <div>
                  <h2>{activeChecklist.name}</h2>
                  <div className="cl-detail-meta">
                    <span className="cl-badge" style={{
                      background: (STATUS_CONFIG[activeChecklist.status]?.color || '#94A3B8') + '20',
                      color: STATUS_CONFIG[activeChecklist.status]?.color || '#94A3B8'
                    }}>{STATUS_CONFIG[activeChecklist.status]?.label}</span>
                    <span>{CATEGORY_LABELS[activeChecklist.category]}</span>
                    <span>{activeChecklist.project_code} - {activeChecklist.project_name}</span>
                  </div>
                </div>
                {canEdit && activeChecklist.status === 'completed' && (
                  <button className="btn btn-sm btn-primary" onClick={approveChecklist}>
                    <Shield size={14} /> Aprobar
                  </button>
                )}
              </div>

              <div className="cl-items">
                {items.map((item, idx) => {
                  const resp = responses[idx] || {};
                  const ItemIcon = ITEM_ICONS[item.type] || CheckCircle;
                  return (
                    <div key={idx} className={`cl-item ${resp.checked ? 'checked' : ''}`}>
                      <div className="cl-item-header">
                        <span className="cl-item-order">{item.order || idx + 1}</span>
                        <span className="cl-item-text">{item.text}</span>
                        {item.required && <span className="cl-required">*</span>}
                        <ItemIcon size={14} style={{ color: '#64748B', flexShrink: 0 }} />
                      </div>
                      {item.helpText && <p className="cl-item-help">{item.helpText}</p>}

                      <div className="cl-item-input">
                        {item.type === 'check' && (
                          <label className="cl-checkbox">
                            <input type="checkbox" checked={!!resp.checked}
                              onChange={e => updateResponse(idx, 'checked', e.target.checked)}
                              disabled={activeChecklist.status === 'approved'} />
                            <span>{resp.checked ? 'Verificado ✓' : 'Sin verificar'}</span>
                          </label>
                        )}
                        {item.type === 'text' && (
                          <textarea className="cl-textarea" rows={2} value={resp.value || ''}
                            onChange={e => updateResponse(idx, 'value', e.target.value)}
                            disabled={activeChecklist.status === 'approved'}
                            placeholder="Escribe aquí..." />
                        )}
                        {item.type === 'number' && (
                          <input type="number" className="cl-input-number" value={resp.value || ''}
                            onChange={e => {
                              updateResponse(idx, 'value', e.target.value);
                              if (e.target.value) updateResponse(idx, 'checked', true);
                            }}
                            disabled={activeChecklist.status === 'approved'}
                            placeholder="Valor" />
                        )}
                        {item.type === 'photo' && (
                          <div className="cl-photo-note">
                            <Camera size={14} />
                            <span>Foto requerida — subir desde app móvil</span>
                            <label className="cl-checkbox" style={{ marginLeft: 'auto' }}>
                              <input type="checkbox" checked={!!resp.checked}
                                onChange={e => updateResponse(idx, 'checked', e.target.checked)}
                                disabled={activeChecklist.status === 'approved'} />
                              <span>OK</span>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {activeChecklist.approved_by_name && (
                <div className="cl-approved-banner">
                  <Shield size={16} /> Aprobado por {activeChecklist.approved_by_name}
                  {activeChecklist.approved_at && ` — ${new Date(activeChecklist.approved_at).toLocaleDateString('es')}`}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nueva Checklist</h3>
              <button className="btn-icon" onClick={() => setShowCreate(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Proyecto *</label>
                <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
                  <option value="">Seleccionar...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Plantilla</label>
                <select value={createForm.templateId} onChange={e => setCreateForm(f => ({ ...f, templateId: e.target.value }))}>
                  <option value="">Personalizada (vacía)</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>
                      [{CATEGORY_LABELS[t.category] || t.category}] {t.name} ({(t.items || []).length} items)
                    </option>
                  ))}
                </select>
              </div>
              {!createForm.templateId && (
                <div className="form-group">
                  <label>Nombre</label>
                  <input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Nombre de la checklist" />
                </div>
              )}
              <div className="form-group">
                <label>Asignar a</label>
                <select value={createForm.assignedTo} onChange={e => setCreateForm(f => ({ ...f, assignedTo: e.target.value }))}>
                  <option value="">Sin asignar</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={createChecklist}>Crear Checklist</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
